import React from 'react';
import { Code, ExternalLink, Zap, MessageCircle, Database, BarChart, Search, FileText } from 'lucide-react';

interface ExampleProject {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  iconColor: string;
  bgColor: string;
  agents: string[];
  complexity: 'beginner' | 'intermediate' | 'advanced';
  githubUrl?: string;
}

export function A2AExampleProjects() {
  const examples: ExampleProject[] = [
    {
      title: "Research Assistant Network",
      description: "A multi-agent system that coordinates research tasks across specialized agents for search, summarization, fact-checking, and citation.",
      icon: Search,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-100",
      agents: ["Search Agent", "Summarization Agent", "Fact-Checking Agent", "Citation Agent"],
      complexity: "intermediate",
      githubUrl: "https://github.com/example/research-assistant-network"
    },
    {
      title: "Data Analysis Pipeline",
      description: "A data processing system where agents handle different stages: collection, cleaning, analysis, and visualization.",
      icon: BarChart,
      iconColor: "text-green-600",
      bgColor: "bg-green-100",
      agents: ["Data Collection Agent", "Data Cleaning Agent", "Analysis Agent", "Visualization Agent"],
      complexity: "advanced",
      githubUrl: "https://github.com/example/data-analysis-pipeline"
    },
    {
      title: "Customer Support System",
      description: "A customer service system with agents for initial triage, knowledge base lookup, and specialized domain experts.",
      icon: MessageCircle,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-100",
      agents: ["Triage Agent", "Knowledge Base Agent", "Technical Support Agent", "Billing Agent"],
      complexity: "intermediate",
      githubUrl: "https://github.com/example/customer-support-system"
    },
    {
      title: "Content Creation Suite",
      description: "A content generation system with specialized agents for ideation, writing, editing, and SEO optimization.",
      icon: FileText,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-100",
      agents: ["Ideation Agent", "Writing Agent", "Editing Agent", "SEO Agent"],
      complexity: "beginner",
      githubUrl: "https://github.com/example/content-creation-suite"
    },
    {
      title: "Database Query Assistant",
      description: "A system that translates natural language to SQL, validates queries, executes them, and formats results.",
      icon: Database,
      iconColor: "text-red-600",
      bgColor: "bg-red-100",
      agents: ["NL-to-SQL Agent", "Query Validation Agent", "Database Execution Agent", "Results Formatting Agent"],
      complexity: "advanced",
      githubUrl: "https://github.com/example/database-query-assistant"
    }
  ];

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'beginner': return 'text-green-600 bg-green-50 border-green-200';
      case 'intermediate': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'advanced': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <Code className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">A2A Example Projects</h2>
            <p className="opacity-90">Real-world multi-agent system examples</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <p className="text-gray-700 mb-6">
          Explore these example projects to see how the A2A protocol enables powerful multi-agent systems. Each project demonstrates different patterns for agent collaboration and communication.
        </p>
        
        <div className="space-y-6">
          {examples.map((example, index) => (
            <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-4 p-4 border-b border-gray-200">
                <div className={`p-3 rounded-lg ${example.bgColor}`}>
                  <example.icon className={`w-6 h-6 ${example.iconColor}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900">{example.title}</h3>
                  <p className="text-sm text-gray-600">{example.description}</p>
                </div>
                <div className={`px-3 py-1 rounded-lg text-xs font-medium border ${getComplexityColor(example.complexity)}`}>
                  {example.complexity}
                </div>
              </div>
              
              <div className="p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  Agent Collaboration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {example.agents.map((agent, agentIndex) => (
                    <div key={agentIndex} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-700">{agent}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">Communication pattern:</span> {example.complexity === 'advanced' ? 'Mesh' : example.complexity === 'intermediate' ? 'Pipeline' : 'Hub and Spoke'}
                  </div>
                  
                  {example.githubUrl && (
                    <a 
                      href={example.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Code
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-3">Building Your Own Multi-Agent System</h3>
          <p className="text-blue-700 mb-4">
            Ready to build your own multi-agent system using the A2A protocol? Follow these steps:
          </p>
          <ol className="text-blue-700 space-y-2 list-decimal list-inside">
            <li>Define the purpose and scope of your multi-agent system</li>
            <li>Identify the specialized agents needed and their capabilities</li>
            <li>Design the communication patterns between agents</li>
            <li>Implement each agent with A2A protocol support</li>
            <li>Test agent interactions individually, then as a complete system</li>
            <li>Deploy your agents and monitor their collaborative performance</li>
          </ol>
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => window.location.href = '/create-agent'}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Building Your Multi-Agent System
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}